/// <reference types="vite/client" />
/**
 * useRealtimeSubscription Hook
 *
 * A robust base hook for Supabase Realtime subscriptions with:
 * - Connection status tracking
 * - Automatic reconnection with exponential backoff
 * - Error handling with Sentry integration (production)
 * - Environment-aware logging (dev only)
 * - Debounced callback execution
 */

import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { captureRealtimeError, captureRealtimeStatus } from '@/lib/sentry';

// Connection status types
export type RealtimeStatus = 'connecting' | 'subscribed' | 'disconnected' | 'error' | 'reconnecting';

// Reconnection delay schedule (exponential backoff)
const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 16000, 30000]; // Max 30s

const getReconnectDelay = (attempt: number): number =>
  RECONNECT_DELAYS[Math.min(attempt, RECONNECT_DELAYS.length - 1)];

// Environment-aware logging
const log = import.meta.env.DEV
  ? (msg: string, data?: unknown) => console.log(`[Realtime] ${msg}`, data ?? '')
  : () => {};

// Table subscription configuration
export interface TableSubscription {
  table: string;
  event?: '*' | 'INSERT' | 'UPDATE' | 'DELETE';
  filter?: string;
  schema?: string;
}

/**
 * Deterministic, value-based key for a `tables` array.
 *
 * Applies exactly the same defaults the channelConfig builder in `subscribe`
 * applies (`event || '*'`, `schema || 'public'`, filter dropped when falsy) so an
 * elided field and an explicitly-passed default produce the same key — otherwise
 * `[{ table: 'x' }]` and `[{ table: 'x', event: '*' }]` would look like different
 * subscriptions and rejoin the channel for nothing.
 *
 * JSON, not delimiter concatenation: filter strings contain `=`, `.` and `,`
 * (`user_id=eq.123`), so any separator worth typing is already inside the data.
 *
 * Order-sensitive on purpose. Sorting would let a genuinely reordered array look
 * unchanged, and the only thing it buys is skipping one correct re-subscribe.
 */
export const serializeTables = (tables: TableSubscription[]): string =>
  JSON.stringify(
    tables.map((t) => [t.table, t.event || '*', t.schema || 'public', t.filter || null])
  );

// Hook options
export interface UseRealtimeSubscriptionOptions {
  /** Unique channel name for this subscription */
  channelName: string;
  /** Whether the subscription is enabled */
  enabled: boolean;
  /** Tables to subscribe to with optional filters */
  tables: TableSubscription[];
  /** Callback when a postgres change is received */
  onPostgresChange?: (
    table: string,
    payload: RealtimePostgresChangesPayload<Record<string, unknown>>
  ) => void;
  /** Optional callback when status changes */
  onStatusChange?: (status: RealtimeStatus) => void;
  /** Debounce time for change callbacks (ms) */
  debounceMs?: number;
}

// Hook return type
export interface UseRealtimeSubscriptionResult {
  /** Current connection status */
  status: RealtimeStatus;
  /** Error if status is 'error' */
  error: Error | null;
  /** Manually unsubscribe */
  unsubscribe: () => void;
  /** Manually retry connection */
  retry: () => void;
  /** Number of reconnection attempts */
  reconnectAttempts: number;
}

/**
 * Base hook for Supabase Realtime subscriptions.
 *
 * ## Stability contract
 *
 * No option has to be memoized. Every one of them is compared by value, so a caller
 * may pass inline literals and arrow functions and still get exactly one channel
 * join per mount:
 *
 * - `tables` — compared by the value-based key from {@link serializeTables}
 *   (table, event, schema and filter, with the same defaults the channel is built
 *   with). A new-but-equal array is a no-op; a real change re-subscribes.
 * - `onPostgresChange` / `onStatusChange` — held in refs and read at call time, so
 *   their identity never reaches a dependency list. A new arrow function every
 *   render is fine; the channel does not notice.
 * - `channelName`, `enabled`, `debounceMs` — primitives, compared by value.
 *
 * The channel is torn down and rejoined only when one of those values actually
 * changes. This is load-bearing, not a nicety: re-subscribing on every render draws
 * `ClientJoinRateLimitReached` from Supabase, and the hook's error path answers that
 * by scheduling a reconnect, which re-renders, which rejoins — a loop that never
 * settles (see 6de1441b). If you add an option here, key it by value too.
 */
export function useRealtimeSubscription(
  options: UseRealtimeSubscriptionOptions
): UseRealtimeSubscriptionResult {
  const {
    channelName,
    enabled,
    tables,
    onPostgresChange,
    onStatusChange,
    debounceMs = 0,
  } = options;

  const [status, setStatus] = useState<RealtimeStatus>('disconnected');
  const [error, setError] = useState<Error | null>(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);

  const channelRef = useRef<RealtimeChannel | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  // Source of truth for the backoff delay. The `reconnectAttempts` state is for
  // render output only (RealtimeStatusIndicator); callbacks must read the ref so
  // they never see a stale closure value.
  const reconnectAttemptsRef = useRef(0);

  // Store callbacks in refs to avoid re-subscriptions
  const onPostgresChangeRef = useRef(onPostgresChange);
  const onStatusChangeRef = useRef(onStatusChange);

  // `tables` is compared by value, never by identity: a caller passing an inline
  // array literal hands us a new object every render, and depending on that
  // identity used to rebuild `subscribe` -> re-run the subscribe effect -> tear the
  // channel down and rejoin it, on every render. Supabase answers a rejoin loop
  // with ClientJoinRateLimitReached, whose error path schedules a reconnect, which
  // re-renders, which rejoins — a loop that never settles (see 6de1441b).
  // So `subscribe` depends on the serialized key and reads the array off a ref.
  const tablesKey = useMemo(() => serializeTables(tables), [tables]);
  const tablesRef = useRef(tables);

  // `subscribe` calls `scheduleReconnect`, and `scheduleReconnect` depends on
  // `subscribe` — listing it as a dep would be a cycle. Going through a ref lets
  // `subscribe` reach the current version while keeping its identity stable.
  const scheduleReconnectRef = useRef<(() => void) | null>(null);

  // Update refs when callbacks change
  useEffect(() => {
    onPostgresChangeRef.current = onPostgresChange;
    onStatusChangeRef.current = onStatusChange;
  }, [onPostgresChange, onStatusChange]);

  // Must stay declared ABOVE the main subscribe effect: effects run in declaration
  // order, so this guarantees `subscribe` reads the tables from the render that
  // triggered it. Deliberately dep-less (every commit) and never assigned during
  // render — a render that React throws away must not mutate the ref.
  useEffect(() => {
    tablesRef.current = tables;
  });

  // Update status helper
  const updateStatus = useCallback((newStatus: RealtimeStatus, err?: Error) => {
    if (!mountedRef.current) return;

    setStatus(newStatus);
    if (err) {
      setError(err);
    } else if (newStatus !== 'error') {
      setError(null);
    }

    // Notify status change callback
    onStatusChangeRef.current?.(newStatus);

    // Track status in Sentry (production only)
    // Map RealtimeStatus to Sentry status (skip 'connecting' as it's not meaningful for tracking)
    if (newStatus !== 'connecting') {
      const sentryStatus = newStatus === 'subscribed' ? 'connected' : newStatus;
      captureRealtimeStatus(
        sentryStatus,
        channelName,
        err ? { error: err.message } : undefined
      );
    }
  }, [channelName]);

  // Debounced change handler
  const handlePostgresChange = useCallback(
    (table: string, payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => {
      if (debounceMs > 0) {
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }
        debounceTimeoutRef.current = setTimeout(() => {
          onPostgresChangeRef.current?.(table, payload);
        }, debounceMs);
      } else {
        onPostgresChangeRef.current?.(table, payload);
      }
    },
    [debounceMs]
  );

  // Cleanup function
  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
    if (channelRef.current) {
      log(`Unsubscribing from ${channelName}`);
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
  }, [channelName]);

  // Subscribe function
  const subscribe = useCallback(() => {
    // Cleanup any existing subscription
    cleanup();

    if (!enabled) {
      updateStatus('disconnected');
      return;
    }

    updateStatus('connecting');

    // Read once, so the listeners, the log line and any error report all describe
    // the same config this channel was actually built from
    const activeTables = tablesRef.current;
    log(`Subscribing to ${channelName}`, { tables: activeTables });

    // Create channel and add postgres_changes listeners
    let channel = supabase.channel(channelName);

    for (const tableConfig of activeTables) {
      const channelConfig: {
        event: '*' | 'INSERT' | 'UPDATE' | 'DELETE';
        schema: string;
        table: string;
        filter?: string;
      } = {
        event: tableConfig.event || '*',
        schema: tableConfig.schema || 'public',
        table: tableConfig.table,
      };

      // Only add filter if it's defined (Supabase doesn't accept undefined)
      if (tableConfig.filter) {
        channelConfig.filter = tableConfig.filter;
      }

      // Use type assertion to handle Supabase's overloaded on() method
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      channel = (channel as any).on(
        'postgres_changes',
        channelConfig,
        (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => {
          console.log(`[Realtime] RAW EVENT - Channel: ${channelName}, Table: ${tableConfig.table}, Event: ${payload.eventType}`, payload);
          log(`Change in ${tableConfig.table}:`, payload.eventType);
          handlePostgresChange(tableConfig.table, payload);
        }
      );
    }

    // Set before subscribing so the status handler can tell whether it is still
    // the live channel (a replaced channel keeps emitting CLOSED for a while)
    channelRef.current = channel;

    // Subscribe and handle status changes
    channel.subscribe((subscriptionStatus, err) => {
      // Ignore late events from a channel we have already replaced - otherwise
      // they schedule reconnects for a connection that is no longer in use
      if (!mountedRef.current || channelRef.current !== channel) return;

      switch (subscriptionStatus) {
        case 'SUBSCRIBED':
          log(`Subscribed to ${channelName}`);
          // The socket can rejoin on its own, so a reconnect may still be pending
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
          }
          reconnectAttemptsRef.current = 0;
          setReconnectAttempts(0);
          updateStatus('subscribed');
          break;

        case 'CHANNEL_ERROR':
          const channelError = err || new Error('Channel subscription error');
          log(`Error subscribing to ${channelName}:`, channelError);

          // Capture in Sentry (production)
          captureRealtimeError(channelError as Error, {
            channelName,
            tables: activeTables.map((t) => t.table),
          });

          updateStatus('error', channelError as Error);
          scheduleReconnectRef.current?.();
          break;

        case 'TIMED_OUT':
          log(`Subscription timed out for ${channelName}`);
          updateStatus('error', new Error('Subscription timed out'));
          scheduleReconnectRef.current?.();
          break;

        case 'CLOSED':
          log(`Channel closed for ${channelName}`);
          if (enabled && mountedRef.current) {
            updateStatus('disconnected');
            scheduleReconnectRef.current?.();
          }
          break;
      }
    });
    // `tablesKey` and not `tables`: rebuild only when the subscription changes by
    // value. `enabled` IS listed - subscribe reads it in its early return and in the
    // CLOSED branch, and every other dep here is now value-stable, so leaving it out
    // would pin those reads to the value from the first render.
  }, [channelName, enabled, tablesKey, cleanup, updateStatus, handlePostgresChange]);

  // Schedule reconnection with exponential backoff
  const scheduleReconnect = useCallback(() => {
    if (!mountedRef.current || !enabled) return;

    // Never leave an orphaned timer behind - a second one would double-subscribe
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    const attempt = reconnectAttemptsRef.current;
    const delay = getReconnectDelay(attempt);

    log(`Scheduling reconnect attempt ${attempt + 1} in ${delay}ms`);
    updateStatus('reconnecting');

    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectTimeoutRef.current = null;
      if (mountedRef.current && enabled) {
        reconnectAttemptsRef.current = attempt + 1;
        setReconnectAttempts(attempt + 1);
        subscribe();
      }
    }, delay);
  }, [enabled, subscribe, updateStatus]);

  // Keep the ref pointing at the current scheduleReconnect (see ref declaration)
  useEffect(() => {
    scheduleReconnectRef.current = scheduleReconnect;
  }, [scheduleReconnect]);

  // Manual retry function
  const retry = useCallback(() => {
    log(`Manual retry for ${channelName}`);
    reconnectAttemptsRef.current = 0;
    setReconnectAttempts(0);
    subscribe();
  }, [channelName, subscribe]);

  // Manual unsubscribe function
  const unsubscribe = useCallback(() => {
    cleanup();
    updateStatus('disconnected');
  }, [cleanup, updateStatus]);

  // Main effect - subscribe when enabled
  useEffect(() => {
    mountedRef.current = true;

    log(`Effect triggered - enabled: ${enabled}, channel: ${channelName}`);

    if (enabled) {
      log(`Enabled is true - subscribing to ${channelName}`);
      subscribe();
    } else {
      log(`Enabled is false - cleaning up ${channelName}`);
      cleanup();
      updateStatus('disconnected');
    }

    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, [enabled, channelName, subscribe, cleanup, updateStatus]);

  return {
    status,
    error,
    unsubscribe,
    retry,
    reconnectAttempts,
  };
}

export default useRealtimeSubscription;
